/**
 * Auto-open preference + brief-time gate helpers for the Daily Brief.
 *
 * All functions are pure (or storage-only side effects).
 * Brief time is anchored to the browser-local clock (setHours) — `profile.timezone`
 * is available if a stricter cross-TZ conversion is later required; out of scope here.
 *
 * Storage key rule: every key is `xavior:`-prefixed (AGENTS.md).
 * Guard localStorage reads with `typeof window !== 'undefined'`.
 */

export const LS_AUTO_OPEN = 'xavior:dailyBrief:autoOpen';

/**
 * Reuse the existing key verbatim — a user who already saw today's brief
 * must not be re-shown (shared with the brief-dock summary-seen tracking).
 */
export const LS_LAST_SHOWN = 'xavior:dailySummary:lastShown';

// ── Auto-open preference ──────────────────────────────────────────────────

/**
 * Returns the stored auto-open preference, or null if none is set.
 * Stored as '1' (true) or '0' (false).
 */
export function getDailyBriefAutoOpenPref(): boolean | null {
  if (typeof window === 'undefined') return null;
  const v = localStorage.getItem(LS_AUTO_OPEN);
  return v === '1' ? true : v === '0' ? false : null;
}

/**
 * Persists the auto-open preference.
 */
export function setDailyBriefAutoOpen(on: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(LS_AUTO_OPEN, on ? '1' : '0');
  }
}

/**
 * Resolves the effective auto-open flag.
 * Explicit stored preference wins; falls back to the onboarding default.
 */
export function readDailyBriefAutoOpen(onboardingCompleted: boolean): boolean {
  return getDailyBriefAutoOpenPref() ?? Boolean(onboardingCompleted);
}

// ── Brief-time helpers ────────────────────────────────────────────────────

const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

/**
 * Returns a Date representing the brief delivery time on the same calendar
 * day as `now`, resolved in priority order:
 *   1. `deliveryTime` — "HH:MM" string (validated)
 *   2. `schedule.hour` / `schedule.minute`
 *   3. Hard default: 08:00 local time
 *
 * Brief time is anchored to the browser-local clock (user's OS TZ in practice).
 */
export function computeBriefTimeToday(
  now: Date,
  deliveryTime?: string,
  schedule?: { hour?: number; minute?: number },
): Date {
  let hour = 8;
  let minute = 0;

  if (deliveryTime && TIME_RE.test(deliveryTime)) {
    const [h, m] = deliveryTime.split(':').map(Number);
    hour = h;
    minute = m;
  } else if (schedule && typeof schedule.hour === 'number') {
    hour = schedule.hour;
    minute = schedule.minute ?? 0;
  }

  const d = new Date(now);
  d.setHours(hour, minute, 0, 0);
  return d;
}

/**
 * Returns true if the brief delivery time for today has been reached or passed.
 */
export function hasBriefTimePassed(
  now: Date,
  deliveryTime?: string,
  schedule?: { hour?: number; minute?: number },
): boolean {
  return now.getTime() >= computeBriefTimeToday(now, deliveryTime, schedule).getTime();
}

// ── Auto-open decision (pure) ─────────────────────────────────────────────

export type AutoOpenAction = 'open' | 'markShown' | 'wait';

/**
 * Pure decision for the once-per-day Daily Brief auto-open (State A).
 *
 * The `action` tells the caller what to do with its session-scoped
 * "already decided" ref and localStorage:
 *   - `wait`      → conditions not yet met; DO NOT mark the ref (retry later).
 *   - `markShown` → won't open today; mark the ref, DO NOT write last-shown LS.
 *   - `open`      → open the brief, write last-shown LS (todayKey), mark the ref.
 *
 * Ordered so the ref-burn and onboarding bugs can't recur:
 *   1. not onboarded          → wait (retry after welcome clears)
 *   2. auto-open off          → markShown (settled: nothing to show today)
 *   3. brief time not passed  → wait (retry once the delivery time arrives)
 *   4. already shown today    → markShown
 *   5. dock not closed        → wait (retry after the dock closes — fixes ref-burn)
 *   6. otherwise              → open
 */
export function decideAutoOpen(p: {
  now: Date;
  onboardingComplete: boolean;
  autoOpen: boolean;
  deliveryTime?: string;
  schedule?: { hour?: number; minute?: number };
  lastShown: string | null;
  dockClosed: boolean;
}): { action: AutoOpenAction; todayKey: string } {
  const todayKey = p.now.toLocaleDateString('en-CA');

  if (!p.onboardingComplete) return { action: 'wait', todayKey };
  if (!p.autoOpen) return { action: 'markShown', todayKey };
  if (!hasBriefTimePassed(p.now, p.deliveryTime, p.schedule)) return { action: 'wait', todayKey };
  if (p.lastShown === todayKey) return { action: 'markShown', todayKey };
  if (!p.dockClosed) return { action: 'wait', todayKey };
  return { action: 'open', todayKey };
}
